import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardFooter,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Badge,
  Spinner,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import Header from "components/Headers/Header.js";
import { get, put } from "utils/api";
import CustomPagination from "./CustomPagination";
import moment from "moment";
import { useSearch } from "views/Contexts/SearchContext";
import { getSafeUrl } from "utils/helpers";
const KycManagement = () => {
  const { token } = useSearch();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [totalData, setTotalData] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedKyc, setSelectedKyc] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const getFullUrl = (url) => {
    if (!url) return "";
    return getSafeUrl(url.replace(/\\/g, "/"));
  };
  const fetchKycList = async () => {
    setLoading(true);
    try {
      const params = {
        page: pageNo,
        limit: 10,
      };
      if (statusFilter !== "all") {
        params.status = statusFilter;
      }

      const response = await get("/api/kyc/list", params, headers);
      setData(response?.data?.rows || []);
      setTotalData(response?.data?.count || 0);
    } catch (error) {
      console.error("Error fetching KYC list:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKycList();
    // eslint-disable-next-line
  }, [pageNo, statusFilter]);

  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const handleViewDetails = (kyc) => {
    setSelectedKyc(kyc);
    setIsModalOpen(true);
  };

  const handleAction = async (id, action) => {
    setActionLoading((prev) => ({ ...prev, [id]: action }));
    try {
      const endpoint = `/api/kyc/${action}/${id}`;
      await put(endpoint, {}, headers);
      fetchKycList();
      if (isModalOpen) {
        setSelectedKyc(prev => ({ ...prev, status: action === 'approve' ? 'approved' : 'rejected' }));
        toggleModal();
      }
    } catch (error) {
      console.error(`Error ${action}ing KYC:`, error);
    } finally {
      setActionLoading((prev) => ({ ...prev, [id]: null }));
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return <Badge color="warning" className="badge-dot"><i className="bg-warning" /> Pending</Badge>;
      case "approved":
        return <Badge color="success" className="badge-dot"><i className="bg-success" /> Approved</Badge>;
      case "rejected":
        return <Badge color="danger" className="badge-dot"><i className="bg-danger" /> Rejected</Badge>;
      default:
        return <Badge color="secondary">{status || "Unknown"}</Badge>;
    }
  };

  const previewDocument = (url) => {
    if (!url) return;
    const fullUrl = getFullUrl(url);
    const isPdf = fullUrl.toLowerCase().endsWith(".pdf");
    if (isPdf) {
      window.open(fullUrl, "_blank");
    } else {
      setSelectedImage(fullUrl);
    }
  };

  return (
    <>
      <style>{`
        .modal-backdrop-blur {
          opacity: 1 !important;
          background-color: rgba(0, 0, 0, 0.4) !important;
          backdrop-filter: blur(8px) !important;
          -webkit-backdrop-filter: blur(8px) !important;
        }
      `}</style>
      <Header title="KYC Management" />
      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow">
              <CardHeader className="border-0 d-flex justify-content-between align-items-center">
                <h3 className="mb-0">KYC Requests</h3>
                <div>
                  <UncontrolledDropdown>
                    <DropdownToggle caret color="secondary" size="sm">
                      Status: {statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}
                    </DropdownToggle>
                    <DropdownMenu right>
                      <DropdownItem onClick={() => { setStatusFilter("all"); setPageNo(1); }}>All</DropdownItem>
                      <DropdownItem onClick={() => { setStatusFilter("pending"); setPageNo(1); }}>Pending</DropdownItem>
                      <DropdownItem onClick={() => { setStatusFilter("approved"); setPageNo(1); }}>Approved</DropdownItem>
                      <DropdownItem onClick={() => { setStatusFilter("rejected"); setPageNo(1); }}>Rejected</DropdownItem>
                    </DropdownMenu>
                  </UncontrolledDropdown>
                </div>
              </CardHeader>
              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th scope="col">User Name</th>
                    <th scope="col">Email</th>
                    <th scope="col">Account Type</th>
                    <th scope="col">EIN/SSN</th>
                    <th scope="col">Status</th>
                    <th scope="col">Submitted Date</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="8" className="text-center py-5">
                        <Spinner color="primary" />
                      </td>
                    </tr>
                  ) : data && data.length > 0 ? (
                    data.map((kyc) => (
                      <tr key={kyc.id}>
                        <td>{kyc.user?.first_name} {kyc.user?.last_name}</td>
                        <td>{kyc.user?.email}</td>
                        <td>{kyc.account_type || "N/A"}</td>
                        <td>{kyc.ein_ssn || "N/A"}</td>
                        <td>{getStatusBadge(kyc.status)}</td>
                        <td>{moment(kyc.createdAt).format("DD-MM-YYYY")}</td>
                        <td>
                          <div className="d-flex align-items-center">
                            <Button color="info" size="sm" onClick={() => handleViewDetails(kyc)}>
                              View
                            </Button>
                            <Button
                              color="success"
                              size="sm"
                              className="ml-2"
                              disabled={kyc.status === "approved" || actionLoading[kyc.id]}
                              onClick={() => handleAction(kyc.id, "approve")}
                            >
                              {actionLoading[kyc.id] === "approve" ? <Spinner size="sm" /> : "Approve"}
                            </Button>
                            <Button
                              color="danger"
                              size="sm"
                              className="ml-2"
                              disabled={kyc.status === "rejected" || actionLoading[kyc.id]}
                              onClick={() => handleAction(kyc.id, "reject")}
                            >
                              {actionLoading[kyc.id] === "reject" ? <Spinner size="sm" /> : "Reject"}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="text-center py-5">No KYC requests found.</td>
                    </tr>
                  )}
                </tbody>
              </Table>
              <CardFooter className="py-4">
                <nav aria-label="...">
                  <CustomPagination
                    totalItems={totalData}
                    itemsPerPage={10}
                    currentPage={pageNo}
                    onPageChange={(page) => setPageNo(page)}
                  />
                </nav>
              </CardFooter>
            </Card>
          </div>
        </Row>
      </Container>

      {/* Details Modal */}
      <Modal isOpen={isModalOpen} toggle={toggleModal} size="lg" centered backdropClassName="modal-backdrop-blur">
        <ModalHeader toggle={toggleModal}>KYC Verification Details</ModalHeader>
        <ModalBody>
          {selectedKyc && (
            <>
              <div className="pl-lg-4">
                <Row>
                  <Col lg="6">
                    <div className="mb-3">
                      <label className="form-control-label text-muted">User Information</label>
                      <div className="h5 mb-0">{selectedKyc.user?.first_name} {selectedKyc.user?.last_name}</div>
                      <div className="text-muted small">{selectedKyc.user?.email}</div>
                    </div>
                    <div className="mb-3">
                      <label className="form-control-label text-muted">Role</label>
                      <div className="h5 mb-0 text-capitalize">{selectedKyc?.role}</div>
                    </div>
                  </Col>
                  <Col lg="6">
                    <div className="mb-3">
                      <label className="form-control-label text-muted">Account Type / ID</label>
                      <div className="h5 mb-0">{selectedKyc.account_type || "N/A"}</div>
                      <div className="text-muted small">EIN/SSN: {selectedKyc.ein_ssn || "N/A"}</div>
                    </div>
                    <div className="mb-3">
                      <label className="form-control-label text-muted">Status & Date</label>
                      <div>{getStatusBadge(selectedKyc.status)}</div>
                      <div className="text-muted small mt-1">Submitted: {moment(selectedKyc.createdAt).format("DD-MM-YYYY HH:mm")}</div>
                    </div>
                  </Col>
                </Row>
              </div>
              <hr className="my-4" />
              <h6 className="heading-small text-muted mb-4">Documents Section</h6>
              <Row>
                {[
                  { label: "Address Proof", key: "address_proof" },
                  { label: "Signed W9", key: "signed_w9" },
                  { label: "Other Document", key: "other_document" }
                ].map((docType, index) => {
                  const doc = selectedKyc.documents?.find(d => d.document_type === docType.key);

                  return (
                    <Col md="4" key={index} className="mb-3">
                      <Card className="card-stats mb-4 mb-xl-0 shadow-sm border">
                        <div className="p-3">
                          <Row>
                            <div className="col">
                              <h5 className="text-uppercase text-muted mb-0">{docType.label}</h5>
                              <span className="h6 font-weight-bold mb-0 text-truncate d-block" style={{ maxWidth: '100%' }}>
                                {doc?.file_name || "Not Uploaded"}
                              </span>
                            </div>
                          </Row>
                          <div className="mt-3">
                            {doc ? (
                              <div className="d-flex justify-content-between">
                                <Button color="primary" outline size="sm" onClick={() => previewDocument(doc.file_url)}>
                                  <i className="fas fa-eye" /> Preview
                                </Button>
                                <a href={getFullUrl(doc.file_url)} download={doc.file_name} className="btn btn-outline-secondary btn-sm" target="_blank" rel="noreferrer">
                                  <i className="fas fa-download" /> Download
                                </a>
                              </div>
                            ) : (
                              <Badge color="secondary" className="w-100">Unavailable</Badge>
                            )}
                          </div>
                        </div>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="success"
            onClick={() => handleAction(selectedKyc.id, "approve")}
            disabled={selectedKyc?.status === "approved" || actionLoading[selectedKyc?.id]}
          >
            {actionLoading[selectedKyc?.id] === "approve" ? <Spinner size="sm" /> : "Approve"}
          </Button>
          <Button
            color="danger"
            onClick={() => handleAction(selectedKyc.id, "reject")}
            disabled={selectedKyc?.status === "rejected" || actionLoading[selectedKyc?.id]}
          >
            {actionLoading[selectedKyc?.id] === "reject" ? <Spinner size="sm" /> : "Reject"}
          </Button>
          <Button color="secondary" onClick={toggleModal}>Close</Button>
        </ModalFooter>
      </Modal>

      {/* Image Preview Modal */}
      <Modal isOpen={!!selectedImage} toggle={() => setSelectedImage(null)} centered size="lg" backdropClassName="modal-backdrop-blur">
        <ModalHeader toggle={() => setSelectedImage(null)}>Document Preview</ModalHeader>
        <ModalBody className="text-center p-0">
          <img src={selectedImage} alt="Preview" style={{ maxWidth: "100%", maxHeight: "80vh" }} />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setSelectedImage(null)}>Close</Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default KycManagement;
